import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ShopGuideVideoInput } from '@/lib/api/shop-guide-videos-api'
import { shopGuideVideosApi } from '@/lib/api/shop-guide-videos-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const shopGuideVideoKeys = {
    all: ['shop-guide-videos'] as const,
    list: ['shop-guide-videos', 'list'] as const,
    manage: ['shop-guide-videos', 'manage'] as const,
}

function toastError(err: unknown, fallback: string) {
    const { title, description } = formatApiError(err, fallback)
    toast.error(title, { description })
}

/** Video đang hiện (màn xem). */
export function useShopGuideVideos() {
    return useQuery({
        queryKey: shopGuideVideoKeys.list,
        queryFn: () => shopGuideVideosApi.list(),
    })
}

/** Mọi video kể cả đang ẩn (tab Quản lý, cần shop_content.write). */
export function useShopGuideVideosManage(options?: { enabled?: boolean }) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: shopGuideVideoKeys.manage,
        queryFn: () => shopGuideVideosApi.manage(),
    })
}

// Lỗi lưu do form tự hiện cạnh ô (setShopServerErrors) nên không toast ở đây.
export function useSaveShopGuideVideo() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            input,
        }: {
            id?: string
            input: ShopGuideVideoInput
        }) =>
            id
                ? shopGuideVideosApi.update(id, input)
                : shopGuideVideosApi.create(input),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: shopGuideVideoKeys.all }),
    })
}

export function useDeleteShopGuideVideo() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopGuideVideosApi.remove(id),
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: shopGuideVideoKeys.all }),
        onError: (err) => toastError(err, m.guideVideo_delete_failed()),
    })
}

export function useReorderShopGuideVideos() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (ids: string[]) => shopGuideVideosApi.reorder(ids),
        onSettled: () =>
            qc.invalidateQueries({ queryKey: shopGuideVideoKeys.all }),
        onError: (err) => toastError(err, m.guideVideo_reorder_failed()),
    })
}
